import { CdkDragDrop, moveItemInArray, CdkDropList, CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
import { Component, DestroyRef, Input, inject, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { CmsContentItem, CmsItem, CmsItemType } from '@coin/shared/cms/util';
import {
  ConfirmationDialogComponent,
  IconButtonComponent,
  V2ButtonComponent,
  V2CheckboxComponent
} from '@coin/shared/core/ui-storybook';
import { ConfirmationDialogConfirm } from '@coin/shared/core/util-models';
import { filter, tap } from 'rxjs/operators';
import { SelectNewContentComponent } from '../../news/select-new-content/select-new-content.component';
import { EmailPreviewComponent } from '../email-preview/email-preview.component';
import { MatTabGroup, MatTab, MatTabLabel } from '@angular/material/tabs';
import { MatIcon } from '@angular/material/icon';
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle,
  MatExpansionPanelContent
} from '@angular/material/expansion';
import { EmailHeaderComponent } from '../email-header/email-header.component';
import { EmailIndexListComponent } from '../email-index-list/email-index-list.component';
import { EmailFootnotesComponent } from '../email-footnotes/email-footnotes.component';
import { SpacerComponent } from '../../news/spacer/spacer.component';
import { HtmlEditorComponent } from '../../shared/html-editor/html-editor.component';
import { DownloadFileComponent } from '../download-file/download-file.component';
import { LineComponent } from '../../news/line/line.component';
import { EmailButtonComponent } from '../email-button/email-button.component';
import { EmailQuoteComponent } from '../email-quote/email-quote.component';
import { EmailSingleDoubleImageComponent } from '../email-single-double-image/email-single-double-image.component';
import { EmailVideoComponent } from '../email-video/email-video.component';
import { EmailMainBodyImageTextComponent } from '../email-main-body-image-text/email-main-body-image-text.component';
import { MatCard, MatCardContent } from '@angular/material/card';
import { EmailSliderComponent } from '../../shared/email-slider/email-slider.component';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'app-cms-edit-email',
  templateUrl: './cms-edit-email.component.html',
  styleUrls: ['./cms-edit-email.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    EmailPreviewComponent,
    MatTabGroup,
    MatTab,
    MatTabLabel,
    MatIcon,
    MatAccordion,
    CdkDropList,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatExpansionPanelContent,
    EmailHeaderComponent,
    IconButtonComponent,
    CdkDrag,
    CdkDragHandle,
    EmailIndexListComponent,
    EmailFootnotesComponent,
    SpacerComponent,
    HtmlEditorComponent,
    DownloadFileComponent,
    LineComponent,
    EmailButtonComponent,
    EmailQuoteComponent,
    EmailSingleDoubleImageComponent,
    EmailVideoComponent,
    EmailMainBodyImageTextComponent,
    V2ButtonComponent,
    MatCard,
    MatCardContent,
    EmailSliderComponent,
    FormsModule,
    V2CheckboxComponent,
    TranslatePipe
  ]
})
export class CmsEditEmailComponent {
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  @Input() item: CmsContentItem;
  public clickedItemId: string;
  public tabIndex = 0;
  public headlineExpansionPanelId = 'headline-expansion-panel';

  public get defaultPadding(): number {
    if (this.item?.en?.content?.length > 0) {
      const bottomPaddings = this.item.en.content.map(item => item.paddingBottom);
      const topPaddings = this.item.en.content.map(item => item.paddingTop);
      // Concate  both array and filter out any NaN and undefined values
      const allPaddings = bottomPaddings
        .concat(topPaddings)
        // Sometime s the paddings are stored as string. Don't really know why
        .map(padding => (typeof padding === 'string' ? Number.parseFloat(padding) : padding))
        .filter(padding => Number.isFinite(padding));
      const areAllPaddingsSame = allPaddings.every(padding => padding === allPaddings[0]);
      if (areAllPaddingsSame) {
        return allPaddings[0];
      }
    }
    return undefined;
  }

  public get isGenericBranding(): boolean {
    return this.item?.en?.sideData?.isGenericBranding;
  }

  public addContent(subItem?: CmsItem): void {
    this.dialog
      .open(SelectNewContentComponent, {
        disableClose: true,
        maxWidth: 'calc(50vw - 50px)',
        data: {
          preselection: [
            'editor',
            'main-body-image-text',
            'spacer',
            'bullet-list',
            'download-file',
            'email-headline',
            'email-button',
            'email-quote',
            'single-or-multi-image',
            'video',
            'line',
            'email-footnotes',
            'index-list'
          ] satisfies CmsItemType[]
        }
      })
      .afterClosed()
      .pipe(
        filter(item => !!item),
        tap(item => {
          if (subItem) {
            const index = this.item.en.content.findIndex(item => item.id === subItem.id);
            this.item.en.content.splice(index, 0, item);
          } else {
            this.item.en.content.push(item);
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  public removeContent(list: CmsItem[], index: number): void {
    this.dialog
      .open(ConfirmationDialogComponent, {
        disableClose: true,
        data: {
          headline: 'general.delete-confirm',
          translate: true,
          msg: 'cms.emails.delete-item-question',
          confirmMsg: 'general.btnDelete',
          cancelMsg: 'general.btnCancel'
        }
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((remove?: ConfirmationDialogConfirm) => {
        if (remove) {
          list.splice(index, 1);
        }
      });
  }

  public drop(event: CdkDragDrop<string[]>, list: CmsItem[]): void {
    moveItemInArray(list, event.previousIndex, event.currentIndex);
  }

  public getSubItemInfoData(item: CmsItem): string {
    switch (item.type) {
      case 'bullet-list':
      case 'email-footnotes':
      case 'editor':
      case 'email-headline':
        return this.getTextFromHTML(item.content);
      case 'cards':
        return `${item.content.length} items`;
      case 'spacer':
        return `${item.height}px`;
      case 'button':
        return item.text;
      case 'main-body-image-text':
      case 'email-button':
      case 'email-quote':
        return item.content.text;
      case 'download-file':
        return `${item.content.length} items`;
      case 'single-or-multi-image':
        return `${item.content.length} items`;
    }
    return '';
  }

  public getItemInfoData(item: CmsContentItem): string {
    return item.en.sideData.mainHeadline ?? ` ${item.en.sideData.subHeadline}`;
  }

  private getTextFromHTML(html: string): string {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  }

  public getItemExpansionPanelId(cmsItem: CmsItem): string {
    return `expansion-panel-${cmsItem.id}`;
  }

  private itemClicked(id: string): void {
    // Reset cl ickedItem before assigning it again -> Otherwise the expansion panels cannot be opened twice via this clickHandler
    this.clickedItemId = undefined;
    setTimeout(() => {
      this.clickedItemId = id;
      this.tabIndex = 0;
      setTimeout(() => {
        document.getElementById(id).scrollIntoView();
      }, 300);
    });
  }

  public previewItemClicked(clickedItem: CmsItem): void {
    this.itemClicked(this.getItemExpansionPanelId(clickedItem));
  }

  public previewHeadlineClicked(): void {
    this.itemClicked(this.headlineExpansionPanelId);
  }

  public onDefaultPaddingChange(newValue: number): void {
    if (newValue !== undefined && this.item?.en?.content) {
      for (const item of this.item.en.content) {
        item.paddingTop = newValue;
        item.paddingBottom = newValue;
      }
    }
  }

  public onGenericBrandingChange(isGenericBranding: boolean): void {
    if (this.item?.en?.sideData) {
      this.item.en.sideData.isGenericBranding = isGenericBranding;
    }
  }
}
